import {
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Error as ErrorIcon, Refresh } from "@mui/icons-material";
import { formatTime } from "@pages/OrderTracker/components/PlanningAutomation/Timer";
import { useAutomationIntegrationDeploymentApiV1GetRedisServerStatusQuery } from "@providers/hop-ord-server/api";
import { useMemo, useState } from "react";

export const RedisStatus = ({ tenantId }: { tenantId: number }) => {
  const [lastServerRefresh, setLastServerRefresh] = useState<Date>(new Date());
  const {
    data: redisServerStatus,
    refetch: refetchServerStatus,
    isFetching: serverStatusFetching,
  } = useAutomationIntegrationDeploymentApiV1GetRedisServerStatusQuery({
    tenantId,
  });

  const statusColor = useMemo(() => {
    if (!redisServerStatus) {
      return "warning";
    }
    return redisServerStatus.connected ? "success" : "error";
  }, [redisServerStatus]);

  const handleRefresh = () => {
    refetchServerStatus();
    setLastServerRefresh(new Date());
  };

  if (!redisServerStatus) {
    return null;
  }

  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body1">Redis Status:</Typography>
          <Chip
            label={redisServerStatus.connected ? "Connected" : "Disconnected"}
            color={statusColor}
            variant="outlined"
            size="small"
          />
          {redisServerStatus.redisVersion && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              v{redisServerStatus.redisVersion}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            Updated: {lastServerRefresh.toLocaleTimeString()}
          </Typography>
          <Tooltip title="Refresh Redis status">
            <IconButton
              onClick={handleRefresh}
              disabled={serverStatusFetching}
              color="primary"
              size="small"
            >
              {serverStatusFetching ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Refresh fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Redis Server Details */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          ml: 2,
        }}
      >
        {redisServerStatus.uptimeInSeconds != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Uptime:
            </Typography>
            <Typography variant="caption">
              {formatTime(redisServerStatus.uptimeInSeconds)}
            </Typography>
          </Box>
        )}

        {redisServerStatus.connectedClients != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Clients:
            </Typography>
            <Typography variant="caption">
              {redisServerStatus.connectedClients}
            </Typography>
          </Box>
        )}

        {redisServerStatus.usedMemoryHuman != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Memory:
            </Typography>
            <Typography variant="caption">
              {redisServerStatus.usedMemoryHuman}
            </Typography>
          </Box>
        )}

        {redisServerStatus.totalCommandsProcessed != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Commands:
            </Typography>
            <Typography variant="caption">
              {redisServerStatus.totalCommandsProcessed.toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>

      {redisServerStatus.error && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            ml: 2,
          }}
        >
          <ErrorIcon color="error" fontSize="small" />
          <Typography variant="caption" color="error">
            {redisServerStatus.error}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
